/* ==========================================================================
   Chroma — global behaviours
   Header drawer, sticky header, country/language selector, utilities.
   ========================================================================== */

function formatMoney(cents) {
  const format = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
  const value = (Number(cents) / 100).toFixed(2);
  const parts = value.split('.');
  const withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return format
    .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${parts[1]}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${parts[1]}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withCommas);
}

function sectionsToRequest() {
  const sections = ['cart-drawer'];
  if (document.getElementById('main-cart-items')) sections.push('main-cart-items');
  return sections.join(',');
}

function debounce(fn, wait) {
  let timer;
  return function debounced(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function trapFocus(container, elementToFocus = container) {
  const focusable = Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  container.__trapHandler = (event) => {
    if (event.code !== 'Tab' && event.key !== 'Tab') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('keydown', container.__trapHandler);
  elementToFocus.focus();
}

function removeTrapFocus(container) {
  if (container && container.__trapHandler) {
    document.removeEventListener('keydown', container.__trapHandler);
    delete container.__trapHandler;
  }
}

/* ---------- Sticky header ---------- */

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.header');
    this.currentScroll = window.scrollY;
    this.type = this.dataset.stickyType || 'on-scroll-up';

    if (this.type === 'none') return;

    this.classList.add('header-wrapper--sticky');
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  onScroll() {
    const scrollY = window.scrollY;

    if (scrollY > 60) {
      this.classList.add('header-wrapper--scrolled');
    } else {
      this.classList.remove('header-wrapper--scrolled');
    }

    if (this.type === 'on-scroll-up') {
      if (scrollY > this.currentScroll && scrollY > 200) {
        this.classList.add('header-wrapper--hidden');
      } else {
        this.classList.remove('header-wrapper--hidden');
      }
    }

    this.currentScroll = scrollY;
  }
}

customElements.define('sticky-header', StickyHeader);

/* ---------- Menu drawer ---------- */

class HeaderDrawer extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.drawer = this.querySelector('.menu-drawer');

    if (!this.details) return;

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    const closeButton = this.querySelector('.menu-drawer__close-button');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));
  }

  onSummaryClick(event) {
    event.preventDefault();
    if (this.details.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.details.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    setTimeout(() => this.drawer.classList.add('menu-drawer--active'));
    trapFocus(this.drawer, this.drawer.querySelector('a, button') || this.drawer);
  }

  close() {
    this.drawer.classList.remove('menu-drawer--active');
    this.summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.drawer);
    setTimeout(() => this.details.removeAttribute('open'), 300);
  }
}

customElements.define('header-drawer', HeaderDrawer);

/* ---------- Localization (country / language) ---------- */

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.disclosure__button');
    this.panel = this.querySelector('.disclosure__list-wrapper');
    this.input = this.querySelector('input[name="country_code"], input[name="locale_code"]');

    if (!this.button) return;

    this.button.addEventListener('click', this.toggle.bind(this));
    this.querySelectorAll('a.disclosure__link').forEach((link) => {
      link.addEventListener('click', this.onItemClick.bind(this));
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.hide();
    });
  }

  toggle() {
    const expanded = this.button.getAttribute('aria-expanded') === 'true';
    this.button.setAttribute('aria-expanded', String(!expanded));
    this.panel.toggleAttribute('hidden', expanded);
  }

  hide() {
    this.button.setAttribute('aria-expanded', 'false');
    this.panel.setAttribute('hidden', '');
  }

  onItemClick(event) {
    event.preventDefault();
    this.input.value = event.currentTarget.dataset.value;
    this.querySelector('form').submit();
  }
}

customElements.define('localization-form', LocalizationForm);

/* ---------- Expose helpers for section and block scripts ---------- */

window.formatMoney = formatMoney;
window.sectionsToRequest = sectionsToRequest;
window.debounce = debounce;
window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;

/* ---------- "Scroll to the buy box" buttons ----------
   Used by CTA buttons that have no link set, so they still do something
   useful instead of being a dead anchor. */

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-scroll-to-buy]');
  if (!trigger) return;

  event.preventDefault();

  const target =
    document.querySelector('buy-buttons') ||
    document.querySelector('product-form') ||
    document.querySelector('.product-details') ||
    document.getElementById('MainContent');

  if (!target) return;

  const header = document.querySelector('.header-wrapper');
  const offset = header ? header.offsetHeight + 12 : 12;
  const top = target.getBoundingClientRect().top + window.scrollY - offset;

  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
});

/* ---------- Button shine ---------- */

document.addEventListener('DOMContentLoaded', () => {
  if (document.body.dataset.buttonShine === 'true') {
    document.body.classList.add('enable-shine');
  }
});
